/**
 * Helpers for the defensive versatility feature.
 *
 * Layout: 3 groups (GUARD / WING / BIG) × 4 cells = 12 cells.
 *
 * State per cell:
 *   "primary" : primarily defends this position
 *   "switch"  : can switch on this position
 *   "cant"    : can't defend this position
 *   null      : not evaluated yet (default for fresh records)
 *
 * Stored as JSON in players.defensive_versatility and fiches_suivi_informe.defensive_versatility.
 */

export const STATES = {
  NONE: null,
  CANT: 'cant',
  SWITCH: 'switch',
  PRIMARY: 'primary'
};

export const GROUPS = [
  {
    key: 'guard',
    labelKey: 'defensiveVersatility.groups.guard',
    positions: ['guard_0', 'guard_1', 'guard_2', 'guard_3']
  },
  {
    key: 'wing',
    labelKey: 'defensiveVersatility.groups.wing',
    positions: ['wing_0', 'wing_1', 'wing_2', 'wing_3']
  },
  {
    key: 'forward',
    labelKey: 'defensiveVersatility.groups.forward',
    positions: ['forward_0', 'forward_1', 'forward_2', 'forward_3']
  },
  {
    key: 'big',
    labelKey: 'defensiveVersatility.groups.big',
    positions: ['big_0', 'big_1', 'big_2', 'big_3']
  }
];

export const POSITIONS = GROUPS.flatMap(g => g.positions);

export const emptyVersatility = () => POSITIONS.reduce((acc, p) => {
  acc[p] = null;
  return acc;
}, {});

// Cycle on click: null → primary (green) → switch (orange) → cant (red) → null
export const nextState = (current) => {
  if (current === STATES.CANT) return STATES.NONE;
  if (current === STATES.SWITCH) return STATES.CANT;
  if (current === STATES.PRIMARY) return STATES.SWITCH;
  return STATES.PRIMARY;
};

const VALID_STATES = new Set([STATES.PRIMARY, STATES.SWITCH, STATES.CANT]);

/**
 * Accepts: object, JSON string, null/undefined.
 * Returns a normalized object with all positions present (missing/invalid → null).
 */
export const normalizeVersatility = (raw) => {
  if (!raw) return emptyVersatility();
  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return emptyVersatility();
    }
  }
  if (typeof value !== 'object') return emptyVersatility();
  const out = emptyVersatility();
  POSITIONS.forEach(p => {
    const v = value[p];
    out[p] = VALID_STATES.has(v) ? v : null;
  });
  return out;
};

export const hasAnyValue = (versatility) => {
  if (!versatility) return false;
  return POSITIONS.some(p => VALID_STATES.has(versatility[p]));
};

export const versatilitySignature = (versatility) => {
  const normalized = normalizeVersatility(versatility);
  return POSITIONS.map(p => normalized[p] || '-').join(',');
};
